// render-picture.ts — builds one picture card's DOM (thumbnail + management buttons),
// reproducing display_item_pictures() / generate_picture_buttons() so the JS gallery is a
// faithful, no-reload equivalent of the old PHP one. Built with createElement/textContent
// (names/descriptions escaped intrinsically); icon HTML comes pre-rendered from icon_map().

import type { ItemMediaConfig, ItemPicture } from './types';
import { runAction } from './actions';

export interface PictureCardCtx {
    config: ItemMediaConfig;
    itemId: string;
    canManage: boolean;
    canHardDelete: boolean;
    scope: HTMLElement; // the pictures grid, for scoping the "demote all other primaries" sweep
    onRemove?: () => void; // called after a card is hidden (soft/hard delete) so badges stay in sync
}

function actionButton(iconHtml: string, cls: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = cls;
    btn.innerHTML = iconHtml; // trusted icon_map() output
    return btn;
}

export function renderPictureCard(pic: ItemPicture, ctx: PictureCardCtx): HTMLElement {
    const { config } = ctx;

    const col = document.createElement('div');
    col.className = 'col-md-3 p-2';
    col.id = 'div_picture_' + pic.pic_id;

    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'd-flex align-items-center justify-content-center';
    const img = document.createElement('img');
    img.className = 'img-thumbnail';
    img.src = pic.url || '';
    img.alt = pic.name || 'Item picture';
    img.loading = 'lazy';
    thumbWrap.appendChild(img);
    col.appendChild(thumbWrap);

    const body = document.createElement('div');

    if (pic.name) {
        const nameEl = document.createElement('div');
        nameEl.className = 'mb-2';
        nameEl.textContent = pic.name;
        body.appendChild(nameEl);
    }

    if (ctx.canManage) {
        const bar = document.createElement('div');
        bar.className = 'my-3 d-flex justify-content-center align-items-center flex-wrap gap-2';

        // Set-primary: non-primary shows btn-primary (click to promote), primary shows btn-info.
        const primaryBtn = actionButton(
            config.icons.primary,
            'btn btn-sm ft-media-pic-primary ' + (pic.primary ? 'btn-info' : 'btn-primary'),
        );
        primaryBtn.addEventListener('click', () => {
            void runAction(config.picPrimaryEndpoint, { pic_id: pic.pic_id }, config.statusDivId);
            ctx.scope.querySelectorAll<HTMLElement>('.ft-media-pic-primary').forEach((b) => {
                b.classList.remove('btn-info');
                b.classList.add('btn-primary');
            });
            primaryBtn.classList.remove('btn-primary');
            primaryBtn.classList.add('btn-info');
        });
        bar.appendChild(primaryBtn);

        // Edit → modify_picture.php
        const editLink = document.createElement('a');
        editLink.href = config.picEditBase + encodeURIComponent(pic.pic_id);
        editLink.className = 'btn btn-secondary btn-sm';
        editLink.innerHTML = config.icons.edit;
        bar.appendChild(editLink);

        // Soft delete (scoped by attached_id so reference copies delete the right row only).
        const delBtn = actionButton(config.icons.delete, 'btn btn-danger btn-sm');
        delBtn.addEventListener('click', () => {
            void runAction(
                config.picDeleteEndpoint,
                { item_type: 'picture', pic_id: pic.pic_id, attached_id: ctx.itemId },
                config.statusDivId,
            );
            col.style.display = 'none';
            ctx.onRemove?.();
        });
        bar.appendChild(delBtn);

        // Hard delete (super admin only): DB row + CDN when last reference to the pic_id.
        if (ctx.canHardDelete) {
            const hardBtn = actionButton(config.icons.delete, 'btn btn-warning btn-sm');
            hardBtn.addEventListener('click', () => {
                void runAction(
                    config.picHardDeleteEndpoint,
                    { pic_id: pic.pic_id, attached_id: ctx.itemId },
                    config.statusDivId,
                );
                col.style.display = 'none';
                ctx.onRemove?.();
            });
            bar.appendChild(hardBtn);
        }

        body.appendChild(bar);
    }

    if (pic.description) {
        const desc = document.createElement('div');
        desc.className = 'border rounded my-2 p-1';
        desc.textContent = pic.description;
        body.appendChild(desc);
    }

    col.appendChild(body);
    return col;
}
